import React from 'react';
import { AlertCircle, FileX, Clock, HelpCircle, ClipboardX, XCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const StudentChallenges = () => {
  const challenges = [
    {
      icon: HelpCircle,
      title: "Overwhelmed by Options",
      description: "With thousands of universities and courses across multiple countries, choosing the right path feels impossible without expert guidance.",
      color: "from-[#FF7043] to-[#F4511E]"
    },
    {
      icon: FileX,
      title: "Complex Documentation",
      description: "Understanding what documents are needed, how to prepare them, and ensuring nothing is missing can be confusing and time-consuming.",
      color: "from-[#1565C0] to-[#0D47A1]"
    },
    {
      icon: Clock,
      title: "Visa Processing Delays",
      description: "Uncertain timelines and unexpected delays create anxiety about missing your course start date and disrupting your entire plan.",
      color: "from-[#8BC34A] to-[#7CB342]"
    },
    {
      icon: AlertCircle,
      title: "Unclear Visa Requirements",
      description: "Ever-changing visa policies and country-specific requirements make it difficult to stay updated and ensure compliance.",
      color: "from-[#FF7043] to-[#8BC34A]"
    },
    {
      icon: ClipboardX,
      title: "Financial Proof Confusion",
      description: "Determining the right amount to show, preparing bank statements, and understanding fund parking rules is stressful for most families.",
      color: "from-[#1565C0] to-[#FF7043]"
    },
    {
      icon: XCircle,
      title: "Fear of Rejection",
      description: "One small mistake can lead to visa rejection, wasting months of preparation and thousands in fees. The fear is real.",
      color: "from-[#F4511E] to-[#0D47A1]"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-5">
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#1565C0] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#FF7043] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-[#FF7043] to-[#F4511E] text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
              We Understand Your Pain Points
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            The Challenges You're Facing
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Every year, thousands of students struggle with these common obstacles. 
            <span className="block mt-2 font-semibold text-[#1565C0]">But you don't have to face them alone.</span>
          </p>
        </div>

        {/* Challenges Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {challenges.map((challenge, index) => {
            const IconComponent = challenge.icon;
            return (
              <Card 
                key={index}
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 bg-white overflow-hidden relative"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Gradient Border Effect */}
                <div className={`absolute inset-0 bg-gradient-to-r ${challenge.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                <div className="absolute inset-[2px] bg-white rounded-lg"></div>
                
                <CardContent className="p-8 relative z-10">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${challenge.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500`}>
                    <IconComponent size={32} className="text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#1565C0] transition-colors">
                    {challenge.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {challenge.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Solution CTA */}
        <div className="bg-gradient-to-r from-[#1565C0] via-[#0D47A1] to-[#1565C0] rounded-3xl p-12 text-center relative overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-grid-white/10"></div>
          <div className="relative z-10">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              We've Solved These Problems for 10,000+ Students
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              With our proven 3-step process and personalized guidance, you can overcome every obstacle and achieve your dream of studying abroad.
            </p>
            <button 
              onClick={() => {
                const element = document.getElementById('process');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-white text-[#1565C0] px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-xl hover:scale-105 inline-flex items-center gap-2"
            >
              See How We Help You
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StudentChallenges;
